import { useState } from "react";

export interface UnsavedChangesGuard {
  /** Whether the guarded form has unsaved changes (set by the form). */
  setDirty: (dirty: boolean) => void;
  /** Run `leave` now, or — with unsaved changes — ask first. */
  attempt: (leave: () => void) => void;
  /** The unsaved-changes dialog is open. */
  confirming: boolean;
  /** A save started from the dialog is in flight. */
  saving: boolean;
  /** Dialog actions. */
  discard: () => void;
  save: () => Promise<void>;
  keepEditing: () => void;
}

/**
 * Holds a "leave" (close, back, cancel) while a form has unsaved changes, so
 * the host can ask first. Discard leaves; Save calls `submit` and leaves only
 * if it succeeded (a failed save keeps the form open with its error).
 */
export function useUnsavedChangesGuard(
  submit: () => Promise<boolean>,
): UnsavedChangesGuard {
  const [dirty, setDirty] = useState(false);
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null);
  const [saving, setSaving] = useState(false);

  const finish = (leave: (() => void) | null): void => {
    setPendingLeave(null);
    setDirty(false);
    leave?.();
  };

  return {
    setDirty,
    attempt: (leave) => {
      if (dirty) setPendingLeave(() => leave);
      else leave();
    },
    confirming: pendingLeave !== null,
    saving,
    discard: () => finish(pendingLeave),
    save: async () => {
      setSaving(true);
      const saved = await submit();
      setSaving(false);
      if (saved) finish(pendingLeave);
      else setPendingLeave(null);
    },
    keepEditing: () => setPendingLeave(null),
  };
}
